/**
 * 3D picking: ray/pickable intersection for selecting doors, windows,
 * openings (wall-plane rects) and free elements (boxes) in the dollhouse view.
 *
 * Pickables are collected while the scene mesh is built (each knows the 2D
 * editor `Selection` it stands for), and a click resolves to the nearest hit
 * whose wall group is currently visible — so you can't select a feature on a
 * culled wall.
 *
 * The floor is a fallback target: only when no solid pickable is hit does the
 * click test floor regions (flooring elements first, the bare room floor last),
 * mirroring the 2D plan's tap order.
 *
 * A host can widen the solid pass with a reach: a tap that hits no solid
 * exactly then takes the one it misses by the least on screen, within that
 * reach past its edges, before the floor. Touch shells pass a touch target's
 * reach on screen.
 */

import { sameLineRef } from '@kaiizen/kai-floor-plan';
import { Selection } from '../editor-model';
import { add, cross, dot3d, rayPlaneDistance, scale, sub, Vec3 } from '../math3d';

export interface PickRay {
  origin: Vec3;
  dir: Vec3;
}

/** A point on the floor plane (y = 0), in world XZ coordinates. */
export interface FloorPoint {
  x: number;
  z: number;
}

/**
 * A click target. `S` is what a hit resolves to — the editor's `Selection` for
 * a single room's scene; the assembler substitutes its own room selection.
 */
export type Pickable3D<S = Selection> =
  & {
    sel: S;
    /** Wall cull group (EDGE_NAMES index), or null = always visible (free elements). */
    group: number | null;
  }
  & (
    | { kind: 'rect'; origin: Vec3; along: Vec3; sLen: number; yLen: number }
    | { kind: 'box'; min: Vec3; max: Vec3 }
    /** A floor region (even-odd over the rings) — fallback pass, first hit wins. */
    | { kind: 'floor'; rings: FloorPoint[][] }
  );

const UP: Vec3 = [0, 1, 0];
/** Click forgiveness around a pickable's edges, in feet. */
const PICK_PAD = 0.05;

/** How far past a pickable's edges a pick still lands, in feet, for a pickable at `distance` along the ray. */
export type PickReach = (distance: number) => number;

const exactReach: PickReach = () => PICK_PAD;

/**
 * The widened solid pass's rings, as fractions of the host's reach, tried in
 * order: a tap goes to the solid it misses by the least on screen, and within
 * one ring to the nearest along the ray.
 */
const REACH_RINGS = [0.25, 0.5, 1];

/**
 * Even-odd containment of the point (`x`, `y`) across `rings`, each a list of
 * (x, y) pairs: inside when a ray from the point crosses the rings' edges an
 * odd number of times.
 */
export function evenOddContains(rings: readonly (readonly (readonly [number, number])[])[], x: number, y: number): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [ax, ay] = ring[i], [bx, by] = ring[j];
      if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
    }
  }
  return inside;
}

/** Ray → wall-plane rectangle (spanned by `along` × world-up), grown by the reach at its distance. */
function rectHit(p: Extract<Pickable3D<unknown>, { kind: 'rect' }>, ray: PickRay, reach: PickReach): number | null {
  const t = rayPlaneDistance(ray.origin, ray.dir, p.origin, cross(p.along, UP));
  if (t === null) return null;
  const d = sub(add(ray.origin, scale(ray.dir, t)), p.origin);
  const s = dot3d(d, p.along), y = d[1];
  const pad = reach(t);
  return s >= -pad && s <= p.sLen + pad && y >= -pad && y <= p.yLen + pad ? t : null;
}

/** How far along the ray the box's nearest corner lies (each axis adding its nearer face), never behind the ray's origin. */
function nearestBoxDepth(p: Extract<Pickable3D<unknown>, { kind: 'box' }>, ray: PickRay): number {
  const depth = [0, 1, 2].reduce(
    (sum, axis) => sum + Math.min((p.min[axis] - ray.origin[axis]) * ray.dir[axis], (p.max[axis] - ray.origin[axis]) * ray.dir[axis]),
    0,
  );
  return Math.max(0, depth);
}

/** Ray → axis-aligned box (slab test), grown by the reach at its nearest depth along the ray, so the growth on screen stays within the reach. */
function boxHit(p: Extract<Pickable3D<unknown>, { kind: 'box' }>, ray: PickRay, reach: PickReach): number | null {
  const pad = reach(nearestBoxDepth(p, ray));
  let t0 = 0, t1 = Infinity;
  for (let i = 0; i < 3; i++) {
    const o = ray.origin[i], d = ray.dir[i];
    if (Math.abs(d) < 1e-12) {
      if (o < p.min[i] - pad || o > p.max[i] + pad) return null;
      continue;
    }
    let a = (p.min[i] - pad - o) / d, b = (p.max[i] + pad - o) / d;
    if (a > b) [a, b] = [b, a];
    t0 = Math.max(t0, a);
    t1 = Math.min(t1, b);
    if (t0 > t1) return null;
  }
  return t0 > 0 ? t0 : null;
}

/** Where the ray crosses the floor plane (y = 0), or null if it never does. */
function floorPlanePoint(ray: PickRay): FloorPoint | null {
  const t = rayPlaneDistance(ray.origin, ray.dir, [0, 0, 0], UP);
  if (t === null) return null;
  return { x: ray.origin[0] + ray.dir[0] * t, z: ray.origin[2] + ray.dir[2] * t };
}

/** Even-odd containment of a floor point across the region's rings. */
function floorHit(p: Extract<Pickable3D<unknown>, { kind: 'floor' }>, at: FloorPoint): boolean {
  return evenOddContains(p.rings.map(ring => ring.map(({ x, z }) => [x, z] as const)), at.x, at.z);
}

/** The nearest visible solid pickable along the ray, grown by `reach`, or null. */
function nearestSolid<S>(pickables: readonly Pickable3D<S>[], ray: PickRay, groupVisible: (group: number) => boolean, reach: PickReach): S | null {
  let best: S | null = null;
  let bestT = Infinity;
  for (const p of pickables) {
    if (p.kind === 'floor') continue;
    if (p.group != null && !groupVisible(p.group)) continue;
    const t = p.kind === 'rect' ? rectHit(p, ray, reach) : boxHit(p, ray, reach);
    if (t != null && t < bestT) {
      bestT = t;
      best = p.sel;
    }
  }
  return best;
}

/** The visible solid the ray misses by the least within `reach` past its edges: the first ring of it that holds any, the nearest along the ray there. */
function leastMissedSolid<S>(pickables: readonly Pickable3D<S>[], ray: PickRay, groupVisible: (group: number) => boolean, reach: PickReach): S | null {
  return REACH_RINGS.reduce<S | null>(
    (picked, fraction) => picked ?? nearestSolid(pickables, ray, groupVisible, distance => fraction * reach(distance)),
    null,
  );
}

/** The first floor region the ray lands in, in registration order (flooring elements topmost-first, the bare room floor last), or null. */
function floorUnder<S>(pickables: readonly Pickable3D<S>[], ray: PickRay): S | null {
  const at = floorPlanePoint(ray);
  return (at && pickables.find(p => p.kind === 'floor' && floorHit(p, at))?.sel) ?? null;
}

/**
 * What the ray picks: the nearest visible solid it hits, else, when the host
 * passes a `reach`, the one it misses by the least on screen within that reach
 * past its edges, else the floor region under it, or null.
 */
export function pickAt<S = Selection>(
  pickables: readonly Pickable3D<S>[],
  ray: PickRay,
  groupVisible: (group: number) => boolean,
  reach?: PickReach,
): S | null {
  return nearestSolid(pickables, ray, groupVisible, exactReach)
    ?? (reach ? leastMissedSolid(pickables, ray, groupVisible, reach) : null)
    ?? floorUnder(pickables, ray);
}

/** Structural equality for the Selection kinds the 3D view can produce. */
export function sameSelection(a: Selection, b: Selection): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'room':
      return true;
    case 'alcove':
      return b.kind === 'alcove' && a.edge === b.edge && a.index === b.index;
    case 'inner':
      return b.kind === 'inner' && a.index === b.index;
    case 'lineSeg':
      return b.kind === 'lineSeg' && a.si === b.si && sameLineRef(a.ref, b.ref);
    default:
      return false;
  }
}
